import { useState } from "react";
import { api } from "../api/apiClient";
import type { BlogPostLanguage, BlogPostSummary, BlogPostTone } from "../api/types";
import { Modal } from "./Modal";

interface Props {
  onClose: () => void;
  onCreated: (created: BlogPostSummary) => void;
}

/**
 * G-82 (OD-44/45): one canonical language per article, chosen here; Shopify
 * translates. Spanish is the default. There is no region any more, and the
 * internal links are optional because the writer looks the catalog up itself.
 */
const LANGUAGES: Array<{ value: BlogPostLanguage; label: string }> = [
  { value: "es", label: "Español" },
  { value: "ca", label: "Català" },
  { value: "en", label: "English" },
  { value: "ru", label: "Русский" },
  { value: "uk", label: "Українська" },
];

const TONES: Array<{ value: BlogPostTone; label: string }> = [
  { value: "LUXURY", label: "Luxury (default)" },
  { value: "WARM", label: "Warm" },
  { value: "EDITORIAL", label: "Editorial" },
  { value: "PLAYFUL", label: "Playful" },
];

export function NewBlogPostModal({ onClose, onCreated }: Props) {
  const [idea, setIdea] = useState("");
  const [language, setLanguage] = useState<BlogPostLanguage>("es");
  const [internalLinks, setInternalLinks] = useState("");
  const [tone, setTone] = useState<BlogPostTone>("LUXURY");
  const [targetWordCount, setTargetWordCount] = useState<number>(1200);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const linkCount = internalLinks
    .split(/[\n,]/)
    .map((s) => s.trim())
    .filter(Boolean).length;

  const canSubmit = idea.trim().length >= 8 && !!language && !submitting;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      const created = await api.createBlogPost({
        idea: idea.trim(),
        language,
        internalLinks: internalLinks.trim(),
        tone,
        targetWordCount,
      });
      onCreated(created);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Could not create blog post");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal title="Write a new Blog Post" onClose={onClose}>
      <form onSubmit={handleSubmit} className="blog-modal-form">
        <div className="blog-modal-field">
          <label htmlFor="bp-idea">
            <strong>Theme / idea</strong>
          </label>
          <textarea
            id="bp-idea"
            rows={3}
            placeholder="e.g. Cómo cuidar un ramo de peonías en casa"
            value={idea}
            onChange={(e) => setIdea(e.target.value)}
            required
            minLength={8}
            maxLength={500}
          />
          <small className="muted">
            What should the article be about? The writer researches what ranks on Google for
            this idea, reads the competitors, checks our catalog and flower facts, and drafts a
            full article for your review.
          </small>
        </div>

        <div className="blog-modal-field">
          <label htmlFor="bp-language">
            <strong>Language</strong>
          </label>
          <select
            id="bp-language"
            value={language}
            onChange={(e) => setLanguage(e.target.value as BlogPostLanguage)}
            required
          >
            {LANGUAGES.map((l) => (
              <option key={l.value} value={l.value}>
                {l.label}
              </option>
            ))}
          </select>
          <small className="muted">
            The language the article is written in. Other languages are translated in Shopify.
          </small>
        </div>

        <div className="blog-modal-field">
          <label htmlFor="bp-links">
            <strong>Internal links</strong>{" "}
            <span className="muted blog-modal-optional">
              (optional)
            </span>
          </label>
          <textarea
            id="bp-links"
            rows={3}
            placeholder={
              "https://www.flowershopbarcelona.com/collections/peonias\nhttps://www.flowershopbarcelona.com/products/ramo-de-peonias"
            }
            value={internalLinks}
            onChange={(e) => setInternalLinks(e.target.value)}
          />
          <small className="muted">
            One URL per line, or comma-separated. Products or collections you want the article to
            point to. Leave empty and the writer picks from the catalog.
            {linkCount > 0 ? ` Currently ${linkCount} link${linkCount === 1 ? "" : "s"} provided.` : ""}
          </small>
        </div>

        <div>
          <button
            type="button"
            className="button plain blog-modal-toggle"
            onClick={() => setAdvancedOpen((o) => !o)}
          >
            {advancedOpen ? "Hide advanced options" : "Show advanced options"}
          </button>
        </div>

        {advancedOpen ? (
          <div className="blog-modal-advanced">
            <div className="blog-modal-field">
              <label htmlFor="bp-tone">
                <strong>Tone</strong>
              </label>
              <select
                id="bp-tone"
                value={tone}
                onChange={(e) => setTone(e.target.value as BlogPostTone)}
              >
                {TONES.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="blog-modal-field">
              <label htmlFor="bp-words">
                <strong>Target word count</strong>
              </label>
              <input
                id="bp-words"
                type="number"
                min={400}
                max={4000}
                step={50}
                value={targetWordCount}
                onChange={(e) => setTargetWordCount(Number(e.target.value))}
              />
            </div>
          </div>
        ) : null}

        {error ? <div className="alert error">{error}</div> : null}

        <div className="blog-modal-actions">
          <button type="button" className="button tinted" onClick={onClose} disabled={submitting}>
            Cancel
          </button>
          <button type="submit" className="button" disabled={!canSubmit}>
            {submitting ? "Starting…" : "Start writing"}
          </button>
        </div>
      </form>
    </Modal>
  );
}
